import { createAccessControl } from 'better-auth/plugins';
import { first } from 'lodash';
import type { UserRole } from '@/db/schema';

export const ac = createAccessControl({
  auth: ['me', 'sign-out', 'change-password', 'force-change-password'],
  dashboard: ['browse'],
  // Página "Meu Perfil" — qualquer usuário autenticado acessa a própria (o `AppLayout`
  // deriva o recurso do 1º segmento da URL pra todo mundo em `_app`, então precisa existir
  // aqui senão `can()` nega geral, admin incluso).
  profile: ['browse'],
  users: ['browse', 'create', 'read', 'update', 'delete'],
  screens: ['browse', 'create', 'read', 'update', 'delete'],
  'payment-methods': ['browse', 'create', 'read', 'update', 'delete'],
  'shirt-types': ['browse', 'create', 'read', 'update', 'delete'],
  'sleeve-types': ['browse', 'create', 'read', 'update', 'delete'],
  'fabric-types': ['browse', 'create', 'read', 'update', 'delete'],
  'print-types': ['browse', 'create', 'read', 'update', 'delete'],
  'collar-types': ['browse', 'create', 'read', 'update', 'delete'],
  customers: ['browse', 'create', 'read', 'update', 'delete'],
  sales: ['browse', 'create', 'read', 'update', 'delete'],
  'sale-items': ['browse', 'create', 'read', 'update', 'delete'],
  'additional-options': ['browse', 'create', 'read', 'update', 'delete'],
  uploads: ['browse', 'create'],
  companies: ['browse', 'create', 'read', 'update', 'delete'],
  'shirt-sizes': ['browse', 'create', 'read', 'update', 'delete'],
  'operational-costs': ['browse', 'create', 'read', 'update', 'delete'],
  'whatsapp-templates': ['browse', 'create', 'read', 'update', 'delete'],
} as const);

export const roles = {
  admin: ac.newRole({
    auth: Object.values(ac.statements.auth),
    dashboard: Object.values(ac.statements.dashboard),
    profile: Object.values(ac.statements.profile),
    users: Object.values(ac.statements.users),
    screens: Object.values(ac.statements.screens),
    'payment-methods': Object.values(ac.statements['payment-methods']),
    'shirt-types': Object.values(ac.statements['shirt-types']),
    'sleeve-types': Object.values(ac.statements['sleeve-types']),
    'fabric-types': Object.values(ac.statements['fabric-types']),
    'print-types': Object.values(ac.statements['print-types']),
    'collar-types': Object.values(ac.statements['collar-types']),
    customers: Object.values(ac.statements.customers),
    sales: Object.values(ac.statements.sales),
    'sale-items': Object.values(ac.statements['sale-items']),
    'additional-options': Object.values(ac.statements['additional-options']),
    uploads: Object.values(ac.statements.uploads),
    companies: Object.values(ac.statements.companies),
    'shirt-sizes': Object.values(ac.statements['shirt-sizes']),
    'operational-costs': Object.values(ac.statements['operational-costs']),
    'whatsapp-templates': Object.values(ac.statements['whatsapp-templates']),
  }),
  user: ac.newRole({
    auth: Object.values(ac.statements.auth),
    dashboard: Object.values(ac.statements.dashboard),
    profile: Object.values(ac.statements.profile),
    screens: ['browse', 'read'],
    users: [],
    'payment-methods': ['browse', 'read', 'create', 'update'],
    'shirt-types': ['browse', 'read'],
    'sleeve-types': ['browse', 'read'],
    'fabric-types': ['browse', 'read'],
    'print-types': ['browse', 'read'],
    'collar-types': ['browse', 'read'],
    customers: ['browse', 'read', 'create'],
    sales: ['browse', 'read', 'create', 'update'],
    'sale-items': ['browse', 'read', 'create', 'update', 'delete'],
    'additional-options': ['browse', 'read'],
    uploads: ['browse', 'create'],
    companies: ['browse', 'read', 'update'],
    'shirt-sizes': ['browse', 'read', 'create', 'update'],
    'operational-costs': ['browse', 'read'],
    // Textos do bot de WhatsApp são um recurso técnico — edição fica admin-only.
    'whatsapp-templates': [],
  }),
};

/**
 * Checa permissão para recursos compilados (users/auth/dashboard/screens), presentes
 * no `ac` estático do better-auth. Este módulo é isomórfico (usado no client e no server),
 * então NÃO pode depender do banco — recursos dinâmicos (criados via editor de screens)
 * são resolvidos só no servidor, por `canWithDynamic` em `src/api/core/permissions.server.ts`.
 */
export function can(role: UserRole, resource: string, action: string) {
  const roleDef = roles[role];

  if (!roleDef) return false;

  if (!(resource in ac.statements)) return false;

  const { success } = (roleDef.authorize as any)({
    [resource]: [action],
  });

  return success;
}

export const findResourceAndAction = (path: string, method: string) => {
  path = path.replace(/^\/api\/v1\//, '');
  const resource = first(path.split('/')) ?? '';

  let action = '';

  if (resource === 'auth') {
    action = path.split('/')[1]!;
  } else {
    if (method === 'GET') action = 'browse';
    else if (method === 'POST') action = 'create';
    else if (method === 'PUT') action = 'update';
    else if (method === 'PATCH') action = 'update';
    else if (method === 'DELETE') action = 'delete';
  }

  // console.log(`${resource}:${action}`);
  return {
    resource,
    action,
  };
};
